/**
 * wsr-api tenant operational client (WSRPDM-0012).
 *
 * Every function returns the contract DTO DIRECTLY — wsr-api has no
 * `{success,data}` envelope (contract §0.5), so this module never calls
 * `unwrap()`. Void mutations (enable toggles) return HTTP 200 with an empty
 * body; `request()` resolves that to `null`, which these wrappers discard by
 * declaring `Promise<void>` and not returning it.
 *
 * No CSRF on this surface (contract §0.2) — `client.ts` already scopes the
 * CSRF double-submit to saas-auth paths; `/admin/platform/**` mutations here
 * need only the bearer token.
 */
import { request } from '../client';
import type {
  IngestionConfigUpdateRequest,
  IngestionTestResult,
  PlatformTenantDetail,
  PlatformTenantsResponse,
  RunNowResult,
  TestRunRequest,
} from './types';

const BASE = '/admin/platform';

/** GET /admin/platform/tenants — flat roster, sorted by slug (contract §1.1). */
export async function listTenants(): Promise<PlatformTenantsResponse> {
  return request<PlatformTenantsResponse>('GET', `${BASE}/tenants`);
}

/** GET /admin/platform/tenants/{tenantSlug} — 404 UNKNOWN_TENANT (contract §1.2). */
export async function getTenant(slug: string): Promise<PlatformTenantDetail> {
  return request<PlatformTenantDetail>('GET', `${BASE}/tenants/${slug}`);
}

/** POST /admin/platform/global-enabled — the platform master switch (contract §1.3). */
export async function setGlobalEnabled(enabled: boolean): Promise<void> {
  await request<unknown>('POST', `${BASE}/global-enabled`, { json: { enabled } });
}

/** POST /admin/platform/tenants/{tenantSlug}/tenant-enabled (contract §1.4). */
export async function setTenantEnabled(slug: string, enabled: boolean): Promise<void> {
  await request<unknown>('POST', `${BASE}/tenants/${slug}/tenant-enabled`, {
    json: { enabled },
  });
}

/**
 * PUT /admin/platform/tenants/{tenantSlug}/services/{jobType} (contract §1.4).
 * 404 unknown tenant; 400 `UNKNOWN_JOB_TYPE` for an unregistered job type.
 */
export async function setServiceEnabled(
  slug: string,
  jobType: string,
  enabled: boolean,
): Promise<void> {
  await request<unknown>('PUT', `${BASE}/tenants/${slug}/services/${jobType}`, {
    json: { enabled },
  });
}

/** POST /admin/platform/tenants/{slug}/jobs/{jobType}/run — WSRAPI-0087 §2. */
export async function runNow(slug: string, jobType: string): Promise<RunNowResult> {
  return request<RunNowResult>('POST', `${BASE}/tenants/${slug}/jobs/${jobType}/run`);
}

/**
 * PUT /admin/platform/tenants/{tenantSlug}/config (contract §1.5). Re-GETs the
 * tenant afterward so callers get a full `PlatformTenantDetail` (the PUT's own
 * response is only the narrower `IngestionConfigView`) — the spec's "re-GET or
 * use response" seam, exercised here as re-GET. 400 `INVALID_INGESTION_CONFIG`
 * carries a structured `field` (see `./errors.ts#wsrApiError`).
 */
export async function updateConfig(
  slug: string,
  body: IngestionConfigUpdateRequest,
): Promise<PlatformTenantDetail> {
  await request<unknown>('PUT', `${BASE}/tenants/${slug}/config`, { json: body });
  return getTenant(slug);
}

/**
 * POST /admin/platform/tenants/{tenantSlug}/test/{sales|attendance|user|ghl-fields}
 * (contract §1.6, §I). ALWAYS HTTP 200 — outcome is in the result's `status`, never
 * treat a FAILED dry-run as an error response.
 */
export async function dryRun(
  slug: string,
  kind: 'sales' | 'attendance' | 'user' | 'ghl-fields',
  body?: TestRunRequest,
): Promise<IngestionTestResult> {
  return request<IngestionTestResult>('POST', `${BASE}/tenants/${slug}/test/${kind}`, {
    json: body ?? {},
  });
}
